import { useMemo, memo, useCallback, FC, ReactNode } from 'react'
import { CommentUid, CommentsThread, PostUid } from 'interfaces/feed'
import { useStoreSelector, useStoreDispatch } from 'helpers/reactReduxHelpers'
import { createCommentThreadSelector, createThreadsRepliesSelector, RepliesMap } from 'selectors/feedSelectors'
import { FeedPostCommentContainer } from 'components/features/socialFeed/FeedPost/FeedPostCommentContainer'
import styles from 'components/features/socialFeed/FeedCommentsThread/FeedCommentsThread.styl'
import { loadCommentThreadActionCreator, commentReplyCancelActionCreator } from 'actions/feedActions'
import { shallowEqual } from 'react-redux'
import { Compose } from 'components/features/socialFeed/Compose/Compose'
import { getReplyComposeId } from 'helpers/composeHelpers'
import Deferred from 'components/utils/Deferred'
import Spinner from 'components/features/pfm/Spinner/Spinner'
import Anchor from 'components/atoms/navigation/Anchor'
import { getFeedCommentAnchorId } from 'helpers/feedHelpers'
import { CommentComposeLayout } from 'components/features/socialFeed/CommentComposeLayout/CommentComposeLayout'
import { SocialProfileEnsure } from 'components/features/socialFeed/SocialProfileEnsure/SocialProfileEnsure'

const DeferredSpinner = Deferred(Spinner, 300)

type FeedCommentsThreadProps = {
  commentUid: CommentUid
  postUid: PostUid
}

type CommentReplyComposeProps = {
  postUid: PostUid
  commentUid: CommentUid
  parentCommentUid: CommentUid
  onSubmit: () => void
}
const CommentReplyCompose: FC<CommentReplyComposeProps> = ({
  postUid,
  commentUid,
  parentCommentUid,
  onSubmit,
}) => {
  const composeId = getReplyComposeId(commentUid)
  return (
    <div className={ styles.comment }>
      <Anchor placement={ getFeedCommentAnchorId(composeId) }>
        <div className={ styles.commentLayout }>
          <SocialProfileEnsure stopPropagation composeId={ composeId }>
            <CommentComposeLayout composeId={ composeId }>
              <Compose
                placeholder='Write a reply…'
                composeId={ composeId }
                postUid={ postUid }
                commentUid={ commentUid }
                parentCommentUid={ parentCommentUid }
                focusOnMount
                onDone={ onSubmit }
              />
            </CommentComposeLayout>
          </SocialProfileEnsure>
        </div>
      </Anchor>
    </div>
  )
}

export const FeedCommentsThread: FC<FeedCommentsThreadProps> = memo(({
  commentUid,
  postUid,
}) => {
  const threadSelector = useMemo(createCommentThreadSelector, [])
  const withCommentUid = useMemo(() => ({ commentUid }), [commentUid])
  const thread = useStoreSelector<CommentsThread | null>((state) => threadSelector(state, withCommentUid), shallowEqual)

  const replyUidsSelector = useMemo(createThreadsRepliesSelector, [])
  const replyUids = useStoreSelector<RepliesMap>((state) => replyUidsSelector(state, withCommentUid), shallowEqual)

  const dispatch = useStoreDispatch()

  const handleLoadMoreClick = useCallback(() => {
    dispatch(loadCommentThreadActionCreator.request({ commentUid, postUid }))
  }, [commentUid, dispatch, postUid])

  const handleSubmit = useCallback<(uid: CommentUid) => void>((uid) => {
    dispatch(commentReplyCancelActionCreator({ commentUid: uid }))
  }, [dispatch])

  const children = useMemo(() => {
    const items: ReactNode[] = []

    if (replyUids[commentUid]) {
      items.push(
        <CommentReplyCompose
          key={ getReplyComposeId(commentUid) }
          postUid={ postUid }
          commentUid={ commentUid }
          parentCommentUid={ commentUid }
          onSubmit={ () => handleSubmit(commentUid) }
        />
      )
    }

    if (!thread) {
      return items
    }
    thread.results.reduce((acc, uid) => {
      acc.push(
        <div key={ uid } className={ styles.comment }>
          <FeedPostCommentContainer commentUid={ uid } parentCommentUid={ commentUid } />
        </div>
      )

      if (replyUids[uid]) {
        items.push(
          <CommentReplyCompose
            key={ getReplyComposeId(uid) }
            postUid={ postUid }
            commentUid={ uid }
            parentCommentUid={ commentUid }
            onSubmit={ () => handleSubmit(uid) }
          />
        )
      }
      return acc
    }, items)

    if (thread.pagination && thread.pagination.next) {
      items.push(
        <div
          key='showMore'
          className={ styles.showMore }
          onClick={ handleLoadMoreClick }
        >
          Show more replies
          { thread.isLoading && (
            <div className={ styles.spinnerWrapper }>
              <DeferredSpinner />
            </div>
          )}
        </div>
      )
    }

    return items
  }, [replyUids, commentUid, thread, postUid, handleSubmit, handleLoadMoreClick])

  // root div required due :first-child :last-child selectors
  return (
    <div>
      { children }
    </div>
  )
})
